import React, { useState } from 'react'
import useInterval from '#src/hooks/useInterval'
import ReactMarkdown from 'react-markdown'
import { Image } from '@deal/components'
import { Experiment } from '@deal/experiment-js'
import FreeShippingIcon from './free-shipping.svg'
import PriceMatchIcon from './price-match.svg'
import { DepartmentConsumer } from '#src/context/department'
import ContentfulQuery from '#src/containers/Contentful'
import { getSpaceFromTemplate } from '#src/services/contentful'
import styles from './styles.css'
import { CategoryType } from '#src/generated/graphql'

interface BannerType {
  iconOne?: { imageUrl: string }
  textOne?: string
  iconTwo?: { imageUrl: string }
  textTwo?: string
}
interface ContentfulResponse {
  content: { banners: BannerType[] }
  loading: boolean
}

const ROTATION_SPEED = 3000

const PageBanner: React.SFC<{}> = () => {
  return (
    <DepartmentConsumer>
      {({ department }) => {
        if (!department) {
          /**
           * If there is no department we don't show a banner
           */
          return null
        } else {
          const { slug } = department
          const splitExperiemnt = `banner-${slug}`

          return (
            <Experiment id={splitExperiemnt} default="off">
              {({ result }) => {
                if (!result || result === 'off') {
                  /**
                   * The experiment isn't set up or it is currently off. This is
                   * more of less a 404 since it's a variation we couldn't find.
                   */
                  return null
                }

                const spaceId = getSpaceFromTemplate('bannerVariations')

                return (
                  <ContentfulQuery space={spaceId} template="bannerVariations" variation={result}>
                    {({ content }: ContentfulResponse) => {
                      if (content && content.banners) {
                        return <Rotate banners={content.banners} />
                      } else if (
                        department.topLevelCategory &&
                        department.topLevelCategory.type === CategoryType.PRODUCT
                      ) {
                        return <DefaultProductBanner />
                      } else {
                        return null
                      }
                    }}
                  </ContentfulQuery>
                )
              }}
            </Experiment>
          )
        }
      }}
    </DepartmentConsumer>
  )
}

export default PageBanner

interface BannerProps {
  banner: BannerType
}

const Banner: React.SFC<BannerProps> = ({ banner: { iconOne, textOne, iconTwo, textTwo } }) => (
  <div className={styles.guaranteeBanner}>
    <span className={styles.guarantee}>
      {iconOne && <Image width={15} height={15} src={iconOne.imageUrl} className={styles.image} />}
      {textOne && <ReactMarkdown source={textOne} />}
    </span>
    {iconTwo || textTwo ? (
      <>
        <div className={styles.break} />
        <span className={styles.guarantee}>
          {iconTwo && (
            <Image width={15} height={15} src={iconTwo.imageUrl} className={styles.image} />
          )}
          {textTwo && <ReactMarkdown source={textTwo} />}
        </span>
      </>
    ) : null}
  </div>
)

const DefaultProductBanner: React.SFC<{}> = () => (
  <div className={styles.guaranteeBanner}>
    <span className={styles.guarantee}>
      <FreeShippingIcon />
      <span className={styles.bold}>Free shipping</span> on orders over $49
    </span>
    <div className={styles.break} />
    <span className={styles.guarantee}>
      <PriceMatchIcon />
      <span className={styles.bold}>Price match</span> guarantee
    </span>
  </div>
)

interface RotateProps {
  banners: BannerType[]
}
const Rotate: React.SFC<RotateProps> = ({ banners }) => {
  const [banner, setBanner] = useState(0)
  // Setting interval delay to null ends interval
  useInterval(() => {
    const nextBanner = banner + 1 === banners.length ? 0 : banner + 1
    setBanner(nextBanner)
  }, ROTATION_SPEED)

  return <Banner banner={banners[banner]} />
}
